/**
 * Retrieves the value from a nested object using a path array.
 * Traverses the object by following each segment in the path.
 *
 * Example:
 *   const obj = { a: { b: { c: 42 } } };
 *   getValueByPath(obj, ['a', 'b', 'c']); // returns 42
 *   getValueByPath(obj, ['a', 'x', 'c']); // returns undefined
 *
 * @param input - The object to traverse.
 * @param path - An array of strings representing the path to the value.
 * @returns The value at the specified path, or undefined if not found.
 */
export const getValueByPath = (input: unknown, path: string[]) => {
  return path.reduce((acc, segment) => {
    if (acc && typeof acc === 'object' && acc !== null && segment in acc) {
      return (acc as Record<string, unknown>)[segment]
    }
    return undefined
  }, input)
}

/**
 * Extracts prefix values from an array of documents using a dot-separated path.
 * For each document, it traverses the object using the provided path and returns the value as a string.
 * If the value is not found, an empty string is returned for that document.
 *
 * Example:
 *   const docs = [
 *     { info: { title: 'API 1' } },
 *     { info: { title: 'API 2' } },
 *     { info: { name: 'No Title' } }
 *   ];
 *   getPrefixes(docs, 'info.title'); // returns ['API 1', 'API 2', '']
 *
 * @param documents - Array of OpenAPI document objects.
 * @param path - Dot-separated string path to extract the value from each document.
 * @returns Array of string values (or empty string if not found) for each document.
 */
export const getPrefixes = (documents: unknown[], path: string): string[] => {
  const segments = path.split('.')

  return documents.map((doc) => {
    const value = getValueByPath(doc, segments)

    if (value === undefined) {
      return ''
    }

    return String(value)
  })
}
